/** @format */

import {ChangeEvent, useState} from 'react';

import QrCode from 'react-qr-code';

import Alert from '@mui/material/Alert';
import {Box, LinearProgress, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import BackHeader from 'components/BackHeader';
import MediumTextField from 'components/inputs/MediumTextField';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';

import {
  useDisableTwoFaMutation,
  useEnableTwoFaMutation,
  useGetCurrentUserQuery,
} from './graphql/__generated__';

const CODE_LENGTH = 6;

const TwoFactorAuth = () => {
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');

  const {t} = useTranslation();

  const {loading, error, data, refetch} = useGetCurrentUserQuery();
  const [enable2Fa, {loading: enable2FaLoading}] = useEnableTwoFaMutation();
  const [disable2Fa, {loading: disable2FaLoading}] = useDisableTwoFaMutation();

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || !data?.currentUser) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const {phone, has2fa, secret} = data.currentUser;

  const renderQrCode = () => {
    // https://hexdocs.pm/nimble_totp/NimbleTOTP.html#otpauth_uri/3
    const label = `AdSensor:${phone}`;
    const query = `secret=${secret}&issuer=AdSensor`;
    const otpauthUri = `otpauth://totp/${label}?${query}`;

    // qr code should have "quit zone"
    // https://qrworld.wordpress.com/2011/08/09/the-quiet-zone/
    return (
      <Box display='block' p={2} bgcolor='white'>
        <QrCode value={otpauthUri} />
      </Box>
    );
  };

  const renderCodeInput = () => {
    if (has2fa) return null;

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setCode(event.target.value);
    };

    return (
      <MediumTextField
        error={Boolean(codeError)}
        helperText={codeError}
        slotProps={{
          htmlInput: {
            maxLength: CODE_LENGTH,
          },
        }}
        placeholder={t('Код аутентификации', 'Код аутентификации')}
        value={code}
        onChange={handleChange}
      />
    );
  };

  const renderButton = () => {
    const handleDisable = async () => {
      await disable2Fa();
      refetch();
    };

    const handleEnable = async () => {
      try {
        await enable2Fa({variables: {secret, code}});
        refetch();
      } catch (e) {
        setCodeError((e as Error).message);
      }
    };

    if (has2fa) {
      return (
        <PrimaryMediumButton disabled={disable2FaLoading} onClick={handleDisable}>
          <Trans>Отключить</Trans>
        </PrimaryMediumButton>
      );
    }

    return (
      <Box mt={4}>
        <PrimaryMediumButton
          disabled={enable2FaLoading || !code || code.length !== CODE_LENGTH}
          onClick={handleEnable}
        >
          <Trans>Включить</Trans>
        </PrimaryMediumButton>
      </Box>
    );
  };

  return (
    <>
      <Box mb={10}>
        <BackHeader />
      </Box>
      <Typography variant='h1'>
        <Trans>Безопасность</Trans>
      </Typography>

      <Box mt={6}>
        {has2fa ? (
          <Typography variant='body1'>
            <Trans>У вас подключена двухфакторная аутентификация.</Trans>
          </Typography>
        ) : (
          <Typography variant='body1'>
            <Trans>
              У вас отключена двухфакторная аутентификация.
              <br />
              <br />
              Для подключения двухфакторной аутентификации отсканируйте QR код с помощью вашего
              приложения для двухфакторной аутентификации и введите код аутентификации в поле ввода.
            </Trans>
          </Typography>
        )}
      </Box>

      <Box display='flex' justifyContent='center' mt={10}>
        {renderQrCode()}
      </Box>

      <Box mt={10}>
        {renderCodeInput()}
        {renderButton()}
      </Box>
    </>
  );
};

export default TwoFactorAuth;
